import { useMemo, useState } from 'react';

import Logo from '../ui/Logo';
import Btn from '../ui/Btn';
import { Icon } from '../ui/Icon';
import { Spinner } from '../ui/primitives';
import { api } from '../../lib/api';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * SmsOptInWelcome — one-time welcome modal shown when a recipient first
 * enters the portal (i.e. when team_members.sms_opt_in_completed === false).
 *
 * The user can either:
 *   1. Enter their phone, check the consent box, and tap "Continue with SMS"
 *      → POSTs { phone_number, opted_in: true }
 *   2. Tap "Skip for now"
 *      → POSTs { phone_number: null, opted_in: false }
 *
 * Either way the backend sets sms_opt_in_completed=true so this modal never
 * reappears for this member. After the request succeeds we call onComplete()
 * and the parent unmounts us.
 *
 * Twilio toll-free verification REQUIRES the consent checkbox to start
 * unchecked. Do not change `consent`'s initial state to true.
 *
 * Props:
 *   teamMember: { id: string, first_name?: string }
 *   onComplete: () => void
 */
export default function SmsOptInWelcome({ teamMember, onComplete }) {
  const isMobile = useIsMobile();
  const firstName = (teamMember?.first_name || '').trim() || 'there';

  // Display value is human-formatted ("+1 (555) 123-4567"). We re-strip to
  // digits on submit and let the backend do the canonical E.164 normalize.
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false); // MUST start unchecked (Twilio)
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // 'continue' | 'skip' | null

  // ── Phone formatting ──
  // Strip non-digits, drop a leading 1 (so "1-555-..." and "+1 555..." both
  // collapse cleanly), cap at 10, then re-render as "+1 (XXX) XXX-XXXX".
  // Lenient on what the user types; strict only on submit-button enablement.
  const formattedPhone = useMemo(() => formatUsPhoneForDisplay(phone), [phone]);
  const digits = useMemo(() => phone.replace(/\D/g, '').replace(/^1/, '').slice(0, 10), [phone]);
  const phoneValid = digits.length === 10;
  const canContinue = phoneValid && consent && !busy;

  const onPhoneChange = (e) => {
    // Save the raw typed string — we re-derive `digits` from it. Storing the
    // raw input means caret behavior stays predictable even when the user
    // deletes a separator.
    setPhone(e.target.value);
  };

  const handleContinue = async () => {
    if (!canContinue) return;
    setBusy('continue');
    setError(null);
    try {
      await api.smsOptIn(teamMember.id, {
        phone_number: '+1' + digits,
        opted_in: true,
      });
      onComplete?.();
    } catch (err) {
      console.error('SMS opt-in failed:', err);
      setError(err.message || 'Could not save your preferences. Please try again.');
      setBusy(null);
    }
  };

  const handleSkip = async () => {
    if (busy) return;
    setBusy('skip');
    setError(null);
    try {
      // Deliberately discard whatever's in the `phone` state — if the user
      // is clicking Skip, declining is a complete decline. We do NOT want a
      // stale phone sitting on the row implying consent the user didn't give.
      await api.smsOptIn(teamMember.id, {
        phone_number: null,
        opted_in: false,
      });
      onComplete?.();
    } catch (err) {
      console.error('SMS skip failed:', err);
      setError(err.message || 'Could not save your preferences. Please try again.');
      setBusy(null);
    }
  };

  // ── Layout shells ──
  // Mobile: full-screen overlay (100vw × 100dvh, no border-radius).
  // Desktop: centered card with translucent backdrop.
  const overlayStyle = isMobile
    ? {
        position: 'fixed', inset: 0,
        background: 'var(--bg)',
        zIndex: 1100,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }
    : {
        position: 'fixed', inset: 0,
        background: 'rgba(10, 15, 46, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
        animation: 'fadeIn 0.2s ease',
      };

  const cardStyle = isMobile
    ? {
        minHeight: '100dvh',
        background: 'var(--surface)',
        display: 'flex', flexDirection: 'column',
        padding: '24px 20px 32px',
        boxSizing: 'border-box',
      }
    : {
        background: 'var(--surface)',
        borderRadius: 'var(--r-lg)',
        maxWidth: 460, width: '100%',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--border)',
        padding: '32px 30px 28px',
        boxSizing: 'border-box',
      };

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        {/* Brand */}
        <div style={{
          display: 'flex', justifyContent: 'center',
          marginBottom: isMobile ? 20 : 18,
        }}>
          <Logo size={28} useFile />
        </div>

        {/* Headline */}
        <h1 style={{
          fontSize: isMobile ? 22 : 22,
          fontWeight: 800, letterSpacing: '-0.02em',
          textAlign: 'center', color: 'var(--text)',
          margin: 0, marginBottom: 6,
        }}>
          Welcome, {firstName}!
        </h1>
        <div style={{
          fontSize: 15, fontWeight: 600,
          textAlign: 'center', color: 'var(--text)',
          marginBottom: 10,
        }}>
          Want updates about your documents?
        </div>
        <p style={{
          fontSize: 13.5, color: 'var(--text-2)',
          textAlign: 'center', lineHeight: 1.5,
          margin: 0, marginBottom: 20,
          maxWidth: 380, marginLeft: 'auto', marginRight: 'auto',
        }}>
          MagicIntake can text you when documents are approved, rejected,
          or expiring. We only send transactional messages — no marketing.
        </p>

        {/* Phone input */}
        <label style={{
          display: 'block',
          fontSize: 12, fontWeight: 600,
          color: 'var(--text-2)',
          marginBottom: 6,
        }}>
          Mobile number
        </label>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="+1 (555) 123-4567"
          value={formattedPhone}
          onChange={onPhoneChange}
          disabled={!!busy}
          style={{
            width: '100%',
            height: 44,
            padding: '0 14px',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            color: 'var(--text)',
            fontSize: 15,
            fontFamily: 'inherit',
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 14,
          }}
        />

        {/* Consent checkbox — MUST default unchecked (Twilio requirement) */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: 12,
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          cursor: 'pointer',
          marginBottom: 12,
        }}>
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            disabled={!!busy}
            style={{
              width: 18, height: 18, marginTop: 1,
              accentColor: 'var(--accent)',
              cursor: 'pointer', flexShrink: 0,
            }}
          />
          <span style={{
            fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5,
          }}>
            Yes, I agree to receive transactional SMS messages from MagicIntake.
            Message and data rates may apply. Reply <strong>STOP</strong> to
            unsubscribe at any time.
          </span>
        </label>

        {/* Disclosure */}
        <p style={{
          fontSize: 11, color: 'var(--text-muted)',
          lineHeight: 1.55, margin: 0, marginBottom: 18,
          textAlign: 'center',
        }}>
          By continuing, you acknowledge our{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent-600)', textDecoration: 'underline' }}
          >Privacy Policy</a>{' '}and{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent-600)', textDecoration: 'underline' }}
          >Terms of Service</a>.{' '}
          You can opt out anytime in settings.
        </p>

        {/* Error banner */}
        {error && (
          <div style={{
            padding: 10,
            background: 'var(--danger-bg)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8,
            color: 'var(--danger)',
            fontSize: 12.5,
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 12,
          }}>
            <Icon.alert size={14} stroke="var(--danger)" />
            <span style={{ color: 'var(--text-2)' }}>{error}</span>
          </div>
        )}

        {/* Spacer pushes buttons toward the bottom on mobile full-screen */}
        {isMobile && <div style={{ flex: 1, minHeight: 8 }} />}

        {/* Actions */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 8,
        }}>
          {/* Skip is rendered first on mobile (stacked under primary) but
              visually we want it second — easiest: keep DOM order, primary
              first. On mobile column the primary appears on top, which is
              the right thing anyway. */}
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            style={{
              order: isMobile ? 1 : 2,
              flex: isMobile ? undefined : 2,
              height: 44,
              padding: '0 18px',
              background: canContinue ? 'var(--accent)' : 'var(--surface-2)',
              color: canContinue ? '#fff' : 'var(--text-muted)',
              border: 'none',
              borderRadius: 10,
              fontSize: 14, fontWeight: 700,
              fontFamily: 'inherit',
              cursor: canContinue ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: canContinue ? '0 4px 12px rgba(61,199,91,0.24)' : 'none',
              transition: 'background 0.15s, box-shadow 0.15s',
            }}
          >
            {busy === 'continue' ? (
              <>
                <Spinner size={14} color="#fff" />
                <span>Saving…</span>
              </>
            ) : (
              <>
                <span>Continue with SMS</span>
                <Icon.arrowR size={15} />
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleSkip}
            disabled={!!busy}
            style={{
              order: isMobile ? 2 : 1,
              flex: isMobile ? undefined : 1,
              height: 44,
              padding: '0 18px',
              background: 'transparent',
              color: 'var(--text-2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              fontSize: 14, fontWeight: 600,
              fontFamily: 'inherit',
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy && busy !== 'skip' ? 0.5 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {busy === 'skip' ? (
              <>
                <Spinner size={14} color="var(--text-2)" />
                <span>Saving…</span>
              </>
            ) : (
              <span>Skip for now</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Phone formatting helper
// ────────────────────────────────────────────────────────────────────────────
// Lenient — accepts whatever the user types and re-formats on each keystroke.
// We strip non-digits, drop a leading "1" (so users who type "1-555-..." or
// paste "+1 555..." get the same result), and cap at 10 digits. Anything
// shorter is rendered partially so the caret doesn't jump strangely.
function formatUsPhoneForDisplay(raw) {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '').replace(/^1/, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3)  return `+1 (${digits}`;
  if (digits.length <= 6)  return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
